//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, FileText, Webhook } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../ThemeContext';

// Single source of truth for a history row - HistoryTab builds these, this
// component renders them. Keep every field here reflected in the JSX (or in
// the click callback's taskInfo) so the contract never carries dead data.
export interface HistoryItem {
  id: string;
  title: string;
  timestamp: string;
  fullTimestamp?: string;
  type: 'chat' | 'task';
  taskIcon?: string;
  taskName?: string;
  taskId?: string;
  webhookName?: string;
}

interface HistoryCardProps {
  item: HistoryItem;
  onHistoryItemClick?: (historyId: string, title: string) => void;
  onDeleteClick?: (item: HistoryItem) => void;
  disabled?: boolean;
}

const HistoryCard: React.FC<HistoryCardProps> = ({
  item,
  onHistoryItemClick,
  onDeleteClick,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useTheme();

  // conversation_id is a UUID column in the database, so the webhook flow's
  // intended id (a "webhook_..." string) never survives storage — the
  // backend hashes it into a plain UUID before it's persisted (see
  // fetch_webhook_names in analyticsai-server/app/repositories.py for the
  // full story). There is no id-shape signal left to check on the frontend:
  // `webhookName` being populated by the backend is the only reliable proof
  // a row came from a webhook.
  const isWebhook = Boolean(item.webhookName);

  // For task rows, and for webhook rows (which are a specialization of task
  // rows — a webhook always triggers exactly one task), the primary title is
  // the name of the thing that ran, not the AI's response preview.
  const displayName = isWebhook
    ? item.webhookName
    : item.type === 'task'
      ? item.taskName
      : undefined;

  const handleCardClick = () => {
    if (disabled) return;
    onHistoryItemClick?.(item.id, item.title);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onDeleteClick?.(item);
  };

  const renderTaskIcon = () => {
    if (item.type !== 'task') return null;

    // Webhooks don't have a task icon of their own (the backend sends "" —
    // see webhooks/routes.py _process_webhook_event), so fall back to the
    // generic Webhook glyph instead of the task-default FileText icon.
    const defaultIcon = isWebhook ? (
      <Webhook
        data-testid="TaskIcon"
        className="shrink-0 text-indigo-500"
        style={{ width: 18, height: 18 }}
      />
    ) : (
      <FileText
        data-testid="TaskIcon"
        className="shrink-0 text-indigo-500"
        style={{ width: 18, height: 18 }}
      />
    );

    // taskIcon may be a JSON string of the full icon object
    // or a legacy raw base64/data-url string
    if (!item.taskIcon) {
      return defaultIcon;
    }
    let ic: {
      name?: string;
      emoji?: string;
      color?: string;
      data?: string;
    } | null = null;
    try {
      ic = JSON.parse(item.taskIcon);
    } catch {
      // Legacy: raw data string
      ic = { data: item.taskIcon };
    }
    if (ic?.emoji) {
      return (
        <span
          id="id-history-card-task-emoji"
          data-testid="history-card-task-emoji"
          style={{ fontSize: 16, flexShrink: 0, lineHeight: 1 }}
        >
          {ic.emoji}
        </span>
      );
    }
    const raw = ic?.data?.trim();
    if (raw) {
      const isUrl = /^(data:|https?:\/\/|\/|\.\/|\.\.\/)/i.test(raw);
      let src = raw;
      if (!isUrl) {
        const icName = (ic?.name || '').toLowerCase();
        let mime = 'image/png';
        if (icName.endsWith('.svg')) mime = 'image/svg+xml';
        else if (icName.endsWith('.jpg') || icName.endsWith('.jpeg'))
          mime = 'image/jpeg';
        else if (icName.endsWith('.gif')) mime = 'image/gif';
        src = `data:${mime};base64,${raw}`;
      }
      return (
        <img
          src={src}
          alt="Task icon"
          style={{ width: 18, height: 18, flexShrink: 0 }}
        />
      );
    }
    return defaultIcon;
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={cn(
        'flex min-h-[60px] cursor-pointer border-t border-gray-200 bg-white transition-colors duration-150 hover:bg-gray-50',
        'first:border-t-0',
        disabled && 'cursor-default',
        darkMode && 'border-[#3f3f3f] bg-[#2d2d2d] hover:bg-[#353535]',
      )}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="items-flex-start flex w-full px-4 py-3">
        <div className="relative flex w-full items-start">
          {/* Left section: icon + title (+ preview line for named task/webhook rows) */}
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {renderTaskIcon()}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'line-clamp-2 min-w-0 break-words text-sm font-normal leading-snug text-neutral-900',
                    darkMode && 'text-[#e0e0e0]',
                  )}
                >
                  {displayName || item.title}
                </span>
                {isWebhook && (
                  <span
                    data-testid="history-card-webhook-badge"
                    title={t('webhook-badge') || 'Webhook'}
                    className={cn(
                      'inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600',
                      darkMode && 'bg-indigo-900/30 text-indigo-300',
                    )}
                  >
                    {t('webhook-badge') || 'Webhook'}
                  </span>
                )}
                {!isWebhook && displayName && (
                  <span
                    data-testid="history-card-task-badge"
                    title={t('task-badge') || 'Task'}
                    className={cn(
                      'inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600',
                      darkMode && 'bg-[#3f3f3f] text-[#bbb]',
                    )}
                  >
                    {t('task-badge') || 'Task'}
                  </span>
                )}
              </div>
              {displayName && (
                <span
                  data-testid="history-card-preview"
                  className={cn(
                    'line-clamp-1 min-w-0 break-words text-xs font-normal text-neutral-500',
                    darkMode && 'text-[#999]',
                  )}
                >
                  {item.title}
                </span>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <span
            title={item.fullTimestamp}
            className={cn(
              'ml-auto mr-4 mt-0.5 shrink-0 whitespace-nowrap text-[13px] font-normal text-neutral-500',
              darkMode && 'text-[#999]',
            )}
          >
            {item.timestamp}
          </span>

          {/* Action buttons */}
          <div className="flex shrink-0 items-start gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'size-8 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                darkMode && 'text-[#777] hover:bg-[#3f3f3f] hover:text-[#bbb]',
              )}
              onClick={handleDeleteClick}
              disabled={disabled}
              title={t('delete') || 'Delete'}
            >
              <Trash2 className="size-[18px]" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryCard;
